import { EnhancedInvoice } from "@/types/invoice";

export interface RepairShop {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  logoUrl?: string;
  createdAt: Date;
}

export interface PDFGenerationOptions {
  includeHeader?: boolean;
  includeLogo?: boolean;
  includeFooter?: boolean;
  pageFormat?: "A4" | "Letter";
  orientation?: "portrait" | "landscape";
}

export class PDFService {
  /**
   * Generate PDF filename for an invoice
   */
  static generateFilename(invoice: EnhancedInvoice): string {
    const date = invoice.createdAt.toISOString().split("T")[0];
    const invoiceNumber = invoice.invoiceNumber.replace(/[^a-zA-Z0-9]/g, "_");
    return `Invoice_${invoiceNumber}_${date}.pdf`;
  }

  /**
   * Format currency for display
   */
  static formatCurrency(amount: number): string {
    return `${amount.toFixed(3)} TND`;
  }

  /**
   * Format date for display
   */
  static formatDate(date: Date): string {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  }

  /**
   * Get status badge color
   */
  static getStatusColor(status: EnhancedInvoice["status"]): string {
    const colors = {
      draft: "#6b7280",
      sent: "#3b82f6",
      paid: "#10b981",
      overdue: "#ef4444",
      cancelled: "#6b7280",
    };
    return colors[status] || colors.draft;
  }

  /**
   * Get payment status color
   */
  static getPaymentStatusColor(
    paymentStatus: EnhancedInvoice["paymentStatus"]
  ): string {
    const colors = {
      unpaid: "#6b7280",
      partial: "#f59e0b",
      paid: "#10b981",
      overpaid: "#3b82f6",
    };
    return colors[paymentStatus] || colors.unpaid;
  }

  /**
   * Calculate invoice totals for display
   */
  static calculateTotals(invoice: EnhancedInvoice) {
    const itemsTotal = invoice.items.reduce(
      (sum, item) => sum + item.totalPrice,
      0
    );
    const subtotal = invoice.subtotal || itemsTotal;
    const taxAmount = invoice.taxAmount || 0;
    const discountAmount = invoice.discountAmount || 0;
    const total = invoice.totalAmount || subtotal + taxAmount - discountAmount;

    return {
      subtotal,
      taxAmount,
      discountAmount,
      total,
      taxRate: invoice.taxRate || 0,
    };
  }

  /**
   * Prepare invoice data for PDF generation
   */
  static prepareInvoiceData(invoice: EnhancedInvoice, repairShop: RepairShop) {
    const totals = this.calculateTotals(invoice);

    return {
      invoice: {
        ...invoice,
        formattedCreatedAt: this.formatDate(invoice.createdAt),
        formattedDueDate: invoice.dueDate
          ? this.formatDate(invoice.dueDate)
          : null,
        statusColor: this.getStatusColor(invoice.status),
        paymentStatusColor: this.getPaymentStatusColor(invoice.paymentStatus),
      },
      repairShop,
      totals: {
        ...totals,
        formattedSubtotal: this.formatCurrency(totals.subtotal),
        formattedTaxAmount: this.formatCurrency(totals.taxAmount),
        formattedDiscountAmount: this.formatCurrency(totals.discountAmount),
        formattedTotal: this.formatCurrency(totals.total),
      },
      items: invoice.items.map((item) => ({
        ...item,
        formattedUnitPrice: this.formatCurrency(item.unitPrice),
        formattedTotalPrice: this.formatCurrency(item.totalPrice),
        formattedDiscountAmount: this.formatCurrency(item.discountAmount || 0),
      })),
    };
  }
}
